import { vn } from 'vend-number'
import { PAYMENT_TYPES } from '@vend/business-components'
import {
  isABPAddedButNotPaid,
  numberOfPaymentsForPaymentType,
} from '../payments'
import { getOutstandingBalance, isReturn } from '../sale'
import { ReduxCurrentSale } from '../../state/slices/currentSale/currentSaleSlice'
import { SaleStatus } from '../../models/sale.constants'

/**
 * requireUnreferencedRefund checks if the current transaction is a return of a sale that does not meet the requirement of a LSPAY referenced refund, or is a blind refund, which both require an LSPAY unreferenced refund
 */
export const requireUnreferencedRefund = (
  amountTenderedNum: string | number,
  sale: Pick<ReduxCurrentSale, 'returnFor' | 'originalSalePayments'>
): boolean => {
  if (isReturn(sale)) {
    return (
      // we can only do a lspay referenced refund if the original sale contains one and only one lspay payment
      numberOfPaymentsForPaymentType({
        payments: sale.originalSalePayments,
        paymentTypeId: PAYMENT_TYPES.LSPAY,
      }) !== 1
    )
  }

  // if merchant enters a negative amount in a non-return sale, it is a blind refund, which also requires lspay unreferenced refund
  return vn(amountTenderedNum).lte(0)
}

/**
 * canApplySurcharge checks if the current payment is with or without surcharge
 */
export const canApplySurcharge = (
  amountTendered: number | string,
  sale: Pick<
    ReduxCurrentSale,
    'payAccountBalance' | 'finals' | 'returnFor' | 'status' | 'payments'
  >,
  hasQbo: boolean,
  hasXero: boolean,
  supportXeroFlag: boolean
): boolean => {
  const outstandingBalance = getOutstandingBalance(sale)

  // surcharge is applicable if
  //  the sale is not an on-account sale
  //  Otherwise, no accounting integration
  //    or the accounting integration is Xero ONLY and the supportXeroFlag is true
  const onaccountCriteria =
    sale.status !== SaleStatus.ACCOUNT_SALE ||
    (!hasXero && !hasQbo) ||
    (hasXero && !hasQbo && supportXeroFlag)

  // if there is no account balance payment (not paid) added to the sale,
  // amount tendered is less than the total balance,
  // amount tendered is greater than 0,
  // and the on-account sale is supported,
  // and amount tendered is less than or equal to the outstanding balance,
  // then surcharge is applicable, otherwise not.
  return (
    !isABPAddedButNotPaid(sale) &&
    !vn(amountTendered).lte(0) &&
    vn(amountTendered).lte(outstandingBalance) &&
    onaccountCriteria
  )
}

/**
 * getTipEligibleAmount calculates the tip eligible amount
 */
export const getTipEligibleAmount = (
  amountTendered: number | string,
  sale: Pick<ReduxCurrentSale, 'finals'>
): number => {
  // If the amount tendered is equal to or greater than (over payment) the total amount of the sale (tax inclusive),
  // then the tip eligible amount is the tax-exclusive price of the sale, return early.
  if (vn(amountTendered).greaterThanOrEqualTo(sale.finals.inclusivePrice)) {
    return vn(sale.finals.exclusivePrice).toNumber()
  }

  // For a $100 pre-tax sale amount and 10% tax Rate, Tax inclusive amount = $110.
  // In case of $55 payment, the tip_eligible_amount should be equal to
  // ($110 - $10) * ( $55 / $110 ) = $50
  return vn(sale.finals.exclusivePrice)
    .mul(vn(amountTendered).div(sale.finals.inclusivePrice))
    .toNumber()
}
